import React from 'react'
import {useSelector} from 'react-redux'
import {DealDetailsButton} from 'components/Accommodation/DealDetails'
import {RoomOfferCardPriceInfo} from 'components/Accommodation/RoomOfferCardPriceInfo'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {getMeta} from 'modules/meta/selectors'
import {
  getAccommodationPageHotelTimezone,
  getHotelApiRoomsParameters
} from 'modules/searchApi/selectors'
import {RootState} from 'store'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Card} from '@vio/atlas/src/components/designSystem/Card'
import {
  getCancellationDeadline,
  getRefundType
} from '@vio/core/src/offer/business/cancellationPenalties'
import {OfferPaymentMode} from '@vio/core/src/offer/business/offerPaymentMode'
import {Offer} from '@vio/core/src/offer/types/offer'
import {updateAllPrices} from '@vio/core/src/room/business/transformSapiRoomsResponse/decoratePrices'
import {Room} from '@vio/core/src/room/types/room'
import {RoundingStrategyVariants} from '@vio/core/src/utils/number'
import {OfferPerks} from '@vio/shared/src/accommodation/OfferPerks/OfferPerks'
import {
  getFrozenBookingsByRoomIds,
  pickFrozenBookingByItinerary
} from '@vio/shared/src/dealFreeze/modules/selector'

import {MAX_CTA_WIDTH_DESKTOP} from '../RoomOffer/RoomOfferLayout'
import {FrozenDealBanner} from './FrozenDealBanner'
import {FrozenRoomOfferCTA} from './FrozenRoomOfferCTA'

interface Props {
  room: Room
}

export const FrozenRoomOffer = ({room}: Props) => {
  const {isMobile, isDesktopXs} = useDeviceLayout()
  const isVerticalOfferLayout = isMobile || isDesktopXs

  const {includeTaxes, includeLocalTaxes} = useSelector(getMeta)
  const hotelId = useSelector(getAccommodationPageHotelId)
  const {checkIn, checkOut, rooms} = useSelector(state =>
    getHotelApiRoomsParameters(state, hotelId)
  )
  const frozenBookings = useSelector((state: RootState) =>
    getFrozenBookingsByRoomIds(state, [room.id, ...(room.squashedIds || [])])
  )
  const frozenBooking = pickFrozenBookingByItinerary(
    frozenBookings,
    checkIn,
    checkOut
  )

  const {
    prices = [],
    cancellationPenalties = [],
    rooms: frozenRooms = []
  } = frozenBooking || {}

  const frozenRoom = frozenRooms?.[0]

  // Parse the prices to be comprehensible by the RoomOfferPrice component
  const params = {
    checkIn,
    checkOut,
    rooms,
    hotelId
  }
  const parsedPrices = prices.map(price =>
    updateAllPrices(price, params, {
      includeTaxes,
      includeLocalTaxes
    })
  )

  const frozenOffer = {
    cancellationPenalties,
    prices: parsedPrices
  } as Offer

  const cancellationDeadline = getCancellationDeadline(cancellationPenalties)
  const refundType = getRefundType(cancellationPenalties)
  // The deal is already booked; finalising it is always paid now.
  const paymentType = OfferPaymentMode.PayNow
  const hotelTimeZone = useSelector(getAccommodationPageHotelTimezone)
  if (!frozenBooking) {
    return null
  }
  return (
    <Card borderColorPath="border.neutral.secondary" borderRadius="lg">
      {!isVerticalOfferLayout && (
        <FrozenDealBanner isStrip expiresAt={frozenBooking.expiresAt} />
      )}
      <Box
        display="flex"
        justifyContent="space-between"
        gap="s500"
        padding={isVerticalOfferLayout ? 's500' : 's600'}
        flexDirection={isVerticalOfferLayout ? 'column' : 'row'}
      >
        <Box display="flex" flexDirection="column" gap="s100">
          <OfferPerks
            paymentType={paymentType}
            refundType={refundType}
            cancellationDeadline={cancellationDeadline}
            mealPlan={frozenRoom?.mealPlan}
            timezone={hotelTimeZone}
          />
          <DealDetailsButton isFrozen roomId={room.id} />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          gap="s300"
          width="100%"
          maxWidth={
            isVerticalOfferLayout ? '100%' : `${MAX_CTA_WIDTH_DESKTOP}px`
          }
        >
          <RoomOfferCardPriceInfo
            hotelId={hotelId}
            offer={frozenOffer}
            roundingStrategy={RoundingStrategyVariants.trunc}
          />
          <Box display="flex" flexDirection="column" gap="s400">
            {isVerticalOfferLayout && (
              <FrozenDealBanner expiresAt={frozenBooking.expiresAt} />
            )}
            <FrozenRoomOfferCTA
              bookingId={frozenBooking.id}
              isCompactLayout={isVerticalOfferLayout}
            />
          </Box>
        </Box>
      </Box>
    </Card>
  )
}
